import { IntlShape } from 'react-intl';

import { isFullRemoteOnlyByFilterCollection } from '@/domain/search/helpers';
import { JobSearchQueryBodyFragment, JobSearchQueryBodySplitBenefitsFragment } from '@/graphql/generated';

import { formatSalary } from './salary-utils';

const filterTypes = {
  JobFilterCompany: 'company',
  JobFilterEmploymentType: 'employmentType',
  JobFilterCareerLevel: 'careerLevel',
  JobFilterDiscipline: 'discipline',
  JobFilterIndustry: 'industry',
  JobFilterBenefit: 'benefit',
  JobFilterBenefitEmployeePerk: 'benefitEmployeePerk',
  JobFilterBenefitWorkingCulture: 'benefitWorkingCulture',
  JobFilterCountry: 'country',
  JobFilterCity: 'city',
  JobFilterRemoteOption: 'remoteOption',
} as const;

export const buildSearchCriteria = (
  searchQueryBody: JobSearchQueryBodyFragment | JobSearchQueryBodySplitBenefitsFragment,
  intl: IntlShape
) => {
  const { keywords, location, filterCollection } = searchQueryBody ?? {};
  const isFullRemote = isFullRemoteOnlyByFilterCollection(filterCollection);
  let hasCompany = false;

  const translatedFilters = (filterCollection ?? [])
    .map((filter) => {
      if (!filter?.__typename) return false;

      if (filter.__typename === 'JobFilterCompany') {
        hasCompany = true;
        return false;
      }
      if (filter.__typename === 'JobFilterSalary' && typeof filter.min === 'number' && typeof filter.max === 'number') {
        return formatSalary(intl.formatNumber, [filter.min, filter.max]);
      }

      if ('entityId' in filter) {
        const filterName = filterTypes[filter.__typename];
        return filter[filterName]?.localizationValue ?? false;
      }
    })
    .filter(Boolean);

  const locationText = location?.city?.name || location?.text;

  const searchKeywords = keywords && keywords.trim();
  const searchLocation =
    !isFullRemote &&
    locationText &&
    `${
      searchKeywords
        ? locationText
        : intl.formatMessage({ id: 'SE_JOBS_SUGGESTED_NAME_LOCATION' }, { location: locationText })
    }${location?.radius ? ` / ${location.radius}km` : ''}`;

  const searchCriteria = [
    ...(searchKeywords ? [searchKeywords] : []),
    ...(searchLocation ? [searchLocation] : []),
    ...(hasCompany ? [intl.formatMessage({ id: 'JOBS_SEARCH_COMPANY_CULTURE_FILTER_VALUE' })] : []),
    ...translatedFilters,
  ].join(' + ');

  return searchCriteria;
};
